import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getStripe } from '@/lib/stripe';
import { Check, Play, Shield, Clock, Users, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import MuseVideoPlayer from '@/components/MuseVideoPlayer';

const VideoPlaceholder = () => (
  <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
    <div className="aspect-video flex items-center justify-center">
      <Play className="h-16 w-16 text-white opacity-80" />
    </div>
  </div>
);

const FeatureList = ({ features }: { features: string[] }) => (
  <ul className="space-y-2">
    {features.map((feature, index) => (
      <li key={index} className="flex items-center gap-3">
        <Check className="h-5 w-5 text-book-yellow flex-shrink-0" />
        <span className="text-book-yellow">{feature}</span>
      </li>
    ))}
  </ul>
);

const Upsell = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isDeclining, setIsDeclining] = useState<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('checkout_session_id');
    if (id) {
      setSessionId(id);
    }
  }, []);

  const isActionDisabled = !sessionId;

  const CREATE_CHECKOUT_URL = 'https://guide.workstreamer.com/api/v1/liberated-consultant/create-checkout-session'

  function getUpsellPriceId(): string {
    return (
      ((import.meta as any).env?.VITE_WORKSTREAMER_CONSULTANT_PRICE_ID as string | undefined) ??
      ((import.meta as any).env?.WORKSTREAMER_CONSULTANT_PRICE_ID as string | undefined) ??
      ''
    )
  }

  async function createUpsellCheckoutSession(existingCheckoutSessionId: string): Promise<{ url?: string; sessionId?: string } | null> {
    const priceId = getUpsellPriceId()
    if (!priceId) {
      console.error('Missing price id (VITE_WORKSTREAMER_CONSULTANT_PRICE_ID)')
      return null
    }
    if (!existingCheckoutSessionId) {
      console.error('Missing required checkout_session_id for upsell checkout')
      return null
    }
    try {
      const successPath = `upsell-success.html`
      const cancelPath = `upsell.html`
      const res = await fetch(CREATE_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ price_id: priceId, checkout_session_id: existingCheckoutSessionId, success_path: successPath, cancel_path: cancelPath, metadata: { upsell_accepted: true } }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Upsell checkout session request failed', { status: res.status, statusText: res.statusText, body: text })
        return null
      }
      const json = await res.json().catch(() => null)
      if (!json) {
        console.error('Failed to parse upsell checkout session response')
        return null
      }
      const result = { url: json?.url, sessionId: json?.sessionId ?? json?.id }
      if (!result.url && !result.sessionId) {
        console.error('No url or sessionId returned from upsell checkout session response')
        return null
      }
      return result
    } catch (error) {
      console.error('Failed to create upsell checkout session', error)
      return null
    }
  }

  async function redirectToCheckout(result: { url?: string; sessionId?: string } | null) {
    if (!result) return
    if (result.url && typeof result.url === 'string') {
      window.location.href = result.url
      return
    }
    if (result.sessionId && typeof result.sessionId === 'string') {
      const stripe = await getStripe()
      await stripe?.redirectToCheckout({ sessionId: result.sessionId })
      return
    }
    console.error('No upsell checkout session url or id returned')
  }

  const handleAcceptOffer = async () => {
    if (!sessionId) {
      console.error('Missing checkout_session_id in URL; cannot proceed with upsell checkout.')
      return
    }
    try {
      setIsAccepting(true)
      const session = await createUpsellCheckoutSession(sessionId)
      await redirectToCheckout(session)
    } finally {
      setIsAccepting(false)
    }
  };

  const handleDeclineOffer = () => {
    if (!sessionId) {
      console.error('Missing checkout_session_id in URL; cannot navigate to downsell.')
      return
    }
    setIsDeclining(true)
    const destination = sessionId
      ? `/downsell.html?checkout_session_id=${encodeURIComponent(sessionId)}`
      : '/downsell.html';
    window.location.href = destination;
  };

  const projectFeatures = [
    "Baseline requirement templates",
    "Built-in client sign-off", 
    "Notes and open item tracking",
    "Version control for revisions"
  ];

  const guideFeatures = [
    "Automatic screenshot capture on every click",
    "Automatic hotspots/arrows added",
    "Easily redact sensitive info",
    "Easily share or export",
    "Easily generate comprehensive user guides"
  ];

  const testingFeatures = [
    "Replace test scripts with screenshot guides",
    "Fewer questions and issues",
    "Easier to conduct testing remotely",
    "Real-time results & analytics"
  ];

  const faqs = [
    {
      question: "Is this a secure platform?",
      answer: "WorkStreamer is an official Oracle Partner and follows the best practice in security including SSL, data encryption, Cloudflare, and more. If you'd like a comprehensive security overview, we can provide you with more documentation and answer any additional questions."
    },
    {
      question: "Can I use it on all my projects?",
      answer: "Yes, the tools enable you to work on an unlimited number of projects. It's recommended to work with your project manager to ensure this tool will be acceptable for use with any specific consulting company and client you work with."
    },
    {
      question: "Does this tool use AI?",
      answer: "Currently there is no AI component but AI features are on the roadmap."
    },
    {
      question: "Does this work with software platforms beyond Oracle?",
      answer: "Yes, WorkStreamer can work for any enterprise software implementations such as Workday, SAP, and more."
    },
    {
      question: "Does the price go up after a year?",
      answer: "No, when you upgrade to this special offer you lock-in the yearly price for WorkStreamer for as long as you stay subscribed."
    }
  ];

  const disabledTooltip = 'Please complete your book order first to unlock this offer.'

  return (
    <>
      <TooltipProvider>
      <Helmet>
        <title>WorkStreamer Accelerator Suite - Special Offer | The Liberated Consultant</title>
        <meta name="description" content="Unlock an entire year of game-changing productivity for less than 2 billable hours. Get the WorkStreamer Accelerator Suite for $197/year." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-book-lightBg">
        {/* Special Offer Bar */}
        <div className="bg-gradient-to-r from-book-yellow to-book-yellow text-book-darkBlue py-3 px-4 text-center">
          <p className="text-sm md:text-base font-semibold">
            🔓 Special offer unlocked - Please select an option on this page to complete your order.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-gray-100 py-4 px-4">
          <div className="container mx-auto">
            <div className="flex items-center justify-center space-x-2 md:space-x-4 text-sm">
              <span className="text-gray-500">STEP 1: OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="font-bold text-book-yellowDark">STEP 2: SPECIAL OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-500">STEP 3: ACCESS</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <section className="bg-white py-6 md:py-10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight max-w-5xl mx-auto">
              Unlock an Entire Year of Game-Changing Productivity for Less Than 2 Billable Hours...
            </h1>
            
            <p className="text-lg text-black mb-8 font-semibold max-w-5xl mx-auto">
              You're only going to see this page once <span className="text-book-yellowDark">(ever)</span> so please read every word <span className="text-book-yellowDark">(and watch the video)</span> very carefully.
            </p>

            {/* Video Section */}
            <div className="mb-8">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-3xl mx-auto">
                <div className="aspect-video">
                  <MuseVideoPlayer videoId="KZJCyez" className="w-full h-full" />
                </div>
              </div>
            </div>

            <p className="text-base text-gray-700 mb-8 max-w-4xl mx-auto">
              Clicking the button will automatically subscribe you to WorkStreamer Suite for $197/year.
            </p>

            {/* Main CTA */}
            <div className="mb-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <Button 
                      onClick={handleAcceptOffer}
                      className="bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 px-4 sm:px-8 text-sm sm:text-lg shadow-lg transition-all duration-300 rounded-md w-full sm:w-auto text-center"
                      size="lg"
                      disabled={isActionDisabled || isAccepting}
                    >
                      {isAccepting ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="block sm:hidden">
                          <span className="block">Yes, Upgrade My Order</span>
                          <span className="block">And Unlock WorkStreamer Suite</span>
                        </span>
                      )}
                      {!isAccepting && (
                        <span className="hidden sm:block">
                          Yes, Upgrade My Order And Unlock WorkStreamer Suite
                        </span>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
            </div>

            {/* Decline Option */}
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <button 
                      onClick={handleDeclineOffer}
                      className="text-gray-600 hover:text-gray-800 underline text-sm"
                      disabled={isActionDisabled || isDeclining || isAccepting}
                    >
                      {isDeclining ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Redirecting...
                        </span>
                      ) : (
                        "No thanks, I don't want this special offer"
                      )}
                    </button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="light-section">
          <div className="container mx-auto px-4">
            <h2 className="section-title text-book-red">
              The Hidden Cost Eroding Consultant's Freedom
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Every week, elite ERP consultants waste 6-12 hours on low-value tasks—hours clients never see or pay for. That's:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg border border-book-yellow/30">
                  <h3 className="font-bold text-book-darkBlue mb-2">Chaotic Email Chains</h3>
                  <p className="text-gray-600">for requirements gathering</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg border border-book-yellow/30">
                  <h3 className="font-bold text-book-darkBlue mb-2">Endless Screenshots</h3>
                  <p className="text-gray-600">to build training guides</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg border border-book-yellow/30">
                  <h3 className="font-bold text-book-darkBlue mb-2">Spreadsheet Nightmares</h3>
                  <p className="text-gray-600">during testing events</p>
                </div>
              </div>
              
              <div className="text-center bg-book-red/10 p-6 rounded-lg border border-book-red/30">
                <p className="text-xl font-bold text-book-red">
                  At $100/hr, that's around $1,000+ of wasted time—every single week.
                </p>
              </div>
            </div>
            

            
           
          </div>
        </section>

        {/* Product Introduction */}
        <section className="dark-section">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-6 text-white">
              Introducing the WorkStreamer Accelerator Suite
            </h2>
            <p className="text-xl text-white/90 text-center mb-16 max-w-4xl mx-auto leading-relaxed">
              Proven tools that reduce documentation effort, speed implementation, and improve client experience. 
              All while being extremely easy to use.
            </p>

            {/* Project Accelerator */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-book-yellow mb-8">1. Project Accelerator</h3>
              <p className="text-lg text-white/90 mb-8">
                Streamline requirements gathering, tracking, and client sign-off with baseline templates, 
                version tracking, and real-time collaboration.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Challenge</h4>
                  <p className="text-white/90 mb-6">
                    Consultants often struggle with requirements management – scattered documents, unclear approval processes, 
                    and lack of real-time visibility lead to delays, scope creep, and client dissatisfaction.
                  </p>
                  
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Solution</h4>
                  <p className="text-white/90">
                    WorkStreamer Project provides a centralized platform for requirements management with a built-in 
                    approval workflow, client collaboration, and baseline templates so nothing falls through the cracks.
                  </p>
                </div>
                <div>
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                    <div className="aspect-video">
                      <MuseVideoPlayer videoId="aeZSZid" className="w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/10 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-book-yellow mb-4">Features:</h4>
                <FeatureList features={projectFeatures} />
              </div>
            </div>

            {/* Guide Accelerator */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-book-yellow mb-8">2. Guide Accelerator</h3>
              <p className="text-lg text-white/90 mb-8">
                Rapidly create professional user guides and job aids with automated screenshot capture capabilities.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Challenge</h4>
                  <p className="text-white/90 mb-6">
                    Creating high-quality training documentation is time-consuming and often results in inconsistent, 
                    unprofessional materials that don't effectively support clients.
                  </p>
                  
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Solution</h4>
                  <p className="text-white/90">
                    WorkStreamer Guide supports the creation of professional training documentation with automated screenshot capture, 
                    in MS Word or online format, and branded deliverables that speed knowledge transfers and reduce support requests.
                  </p>
                </div>
                <div>
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                    <div className="aspect-video">
                      <MuseVideoPlayer videoId="NjoNs2Z" className="w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/10 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-book-yellow mb-4">Features:</h4>
                <FeatureList features={guideFeatures} />
              </div>
            </div>

            {/* Testing Accelerator */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-book-yellow mb-8">3. Testing Accelerator</h3>
              <p className="text-lg text-white/90 mb-8">
                Replace confusing test scripts with visual step-by-step screenshot guides that improve testing efficiency and reduce issues.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Challenge</h4>
                  <p className="text-white/90 mb-6">
                    Traditional text-heavy test scripts are confusing, lead to frequent questions during testing events, 
                    and result in low pass rates and frustrated testers.
                  </p>
                  
                  <h4 className="text-xl font-bold text-book-yellow mb-4">The Solution</h4>
                  <p className="text-white/90">
                    WorkStreamer Testing transforms testing events with visual, step-by-step screenshot guides that are intuitive to follow, 
                    reduce questions, and improve testing efficiency and success rates.
                  </p>
                </div>
                <div>
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                    <div className="aspect-video">
                      <MuseVideoPlayer videoId="5VoW5He" className="w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/10 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-book-yellow mb-4">Features:</h4>
                <FeatureList features={testingFeatures} />
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="gradient-section">
          <div className="container mx-auto px-4 text-center">
            <h2 className="section-title">Results:</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-book-yellow/30">
                <div className="text-4xl font-bold text-book-yellow mb-4">22%</div>
                <p className="text-book-darkBlue font-semibold">reduction during requirements gathering / design</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg border border-book-yellow/30">
                <div className="text-4xl font-bold text-book-yellow mb-4">68%</div>
                <p className="text-book-darkBlue font-semibold">reduction in time spent creating admin user guides</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg border border-book-yellow/30">
                <div className="text-4xl font-bold text-book-yellow mb-4">56%</div>
                <p className="text-book-darkBlue font-semibold">reduction in time spent preparing for and managing testing events</p>
              </div>
            </div>
            
            <div className="text-3xl font-bold text-book-darkBlue">
              Guaranteed.
            </div>
            
            <div className="mt-12">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <Button 
                      onClick={handleAcceptOffer}
                      className="bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 px-4 sm:px-8 text-sm sm:text-lg uppercase tracking-wide shadow-lg transition-all duration-300 rounded-md mb-4 w-full sm:w-auto text-center"
                      size="lg"
                      disabled={isActionDisabled || isAccepting}
                    >
                      {isAccepting ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="block sm:hidden">
                          <span className="block">Yes, Add the WorkStreamer Suite</span>
                          <span className="block">to my Order for $197/Year</span>
                        </span>
                      )}
                      {!isAccepting && (
                        <span className="hidden sm:block">
                          Yes, Add the WorkStreamer Suite to my Order for $197/Year
                        </span>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                      <button 
                        onClick={handleDeclineOffer}
                        className="text-white/70 hover:text-white underline"
                        disabled={isActionDisabled || isDeclining || isAccepting}
                      >
                        {isDeclining ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Redirecting...
                          </span>
                        ) : (
                          'No, I want to pass on this discounted offer'
                        )}
                      </button>
                    </span>
                  </TooltipTrigger>
                  {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
                </Tooltip>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="light-section">
          <div className="container mx-auto px-4">
            <h2 className="section-title">Consultants Love WorkStreamer</h2>
            
            <div className="max-w-3xl mx-auto">
              <div className="testimonial-card text-center">
                <p className="text-lg italic mb-6 leading-relaxed">
                  "I can't imagine creating guides without WorkStreamer now. My client loves how much training I provide them plus it saves me time answering follow-up questions."
                </p>
                <div className="font-bold text-book-darkBlue text-lg">Austin Prado</div>
                <div className="text-gray-600">Oracle HCM Talent and Learning Consultant</div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="dark-section">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8">
              A One-Time Invitation for Action-Takers
            </h2>
            
            <div className="max-w-2xl mx-auto mb-12">
              <div className="bg-white/10 p-8 rounded-lg border border-book-yellow/30">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xl text-white/70 line-through">Regular Investment: $1,000/year</span>
                </div>
                <div className="text-4xl font-bold text-book-yellow mb-4">Today Only: $197/year</div>
                <p className="text-white/90 text-lg">
                  That's 80% off—less than two billable hours—to reclaim hundreds of billable hours throughout the year.
                </p>
              </div>
            </div>

            <div className="mb-12">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Shield className="h-8 w-8 text-book-yellow" />
                <h3 className="text-2xl font-bold text-white">Iron-Clad, 365-Day Money-Back Promise</h3>
              </div>
              <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
                Put WorkStreamer to work. If you don't think it's worth ten times what it costs, just reach out. 
                My team will refund every penny. No questions asked.
              </p>
            </div>

            <div className="bg-book-yellow/20 p-6 rounded-lg mb-12 border border-book-yellow/50">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Clock className="h-6 w-6 text-book-yellow" />
                <p className="text-lg font-bold text-white">
                  Remember: Once you leave this page, the price reverts to $1,000. Delay costs dollars—and freedom.
                </p>
              </div>
            </div>

            <p className="text-lg text-white/90 mb-8 max-w-4xl mx-auto leading-relaxed">
              The fastest path to consultant freedom is efficiency. Tools that compress time let you deliver more value, 
              serve more clients, and command premium rates. Grab the WorkStreamer Accelerator Suite for $197 while you still can.
            </p>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                  <Button 
                    onClick={handleAcceptOffer}
                    className="bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 px-4 sm:px-8 text-sm sm:text-lg uppercase tracking-wide shadow-lg transition-all duration-300 rounded-md mb-4 w-full sm:w-auto text-center"
                    size="lg"
                    disabled={isActionDisabled || isAccepting}
                  >
                    {isAccepting ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="block sm:hidden">
                        <span className="block">Yes, Add the WorkStreamer Suite</span>
                        <span className="block">to my Order for $197/Year</span>
                      </span>
                    )}
                    {!isAccepting && (
                      <span className="hidden sm:block">
                        Yes, Add the WorkStreamer Suite to my Order for $197/Year
                      </span>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
            </Tooltip>
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <button 
                      onClick={handleDeclineOffer}
                      className="text-white/70 hover:text-white underline"
                      disabled={isActionDisabled || isDeclining || isAccepting}
                    >
                      {isDeclining ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Redirecting...
                        </span>
                      ) : (
                        'No, I want to pass on this discounted offer'
                      )}
                    </button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="light-section">
          <div className="container mx-auto px-4">
            <h2 className="section-title">Frequently Asked Questions</h2>
            
            <div className="max-w-4xl mx-auto space-y-8">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg border border-book-yellow/30">
                  <h3 className="text-xl font-bold text-book-darkBlue mb-3">Q: {faq.question}</h3>
                  <p className="text-gray-700 leading-relaxed">A: {faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      </TooltipProvider>
    </>
  );
};

export default Upsell;